import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@exhousing/design-system";
import { Pencil } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { useUpdateTenant } from "@/hooks/api/useParties";
import type { components } from "@/types/api";

type Prefs = components["schemas"]["TenantPreferences"];

// Mirrors the legacy `pref_*` columns; keys align with the matching engine.
const TEXT_FIELDS = [
  { key: "city", label: "City", placeholder: "e.g. Amsterdam" },
  { key: "city_area", label: "Area / district", placeholder: "e.g. Jordaan" },
  { key: "object_type", label: "Property type", placeholder: "e.g. apartment" },
  {
    key: "return_state",
    label: "Furnishing",
    placeholder: "furnished / unfurnished",
  },
] as const;
const NUM_FIELDS = [
  { key: "min_price", label: "Min price (€/mo)" },
  { key: "max_price", label: "Max price (€/mo)" },
  { key: "min_bedrooms", label: "Min bedrooms" },
  { key: "min_surface", label: "Min surface (m²)" },
] as const;

function summary(p: Prefs): string {
  const parts: string[] = [];
  if (p.city) parts.push(p.city + (p.city_area ? ` · ${p.city_area}` : ""));
  if (p.min_price != null || p.max_price != null)
    parts.push(`€${p.min_price ?? 0}–${p.max_price ?? "∞"}`);
  if (p.min_bedrooms != null) parts.push(`${p.min_bedrooms}+ bed`);
  if (p.min_surface != null) parts.push(`${p.min_surface}+ m²`);
  if (p.object_type) parts.push(p.object_type);
  if (p.return_state) parts.push(p.return_state);
  return parts.join(" · ");
}

export function PreferencesCard({
  tenantId,
  preferences,
}: {
  tenantId: string;
  preferences: Prefs;
}) {
  const update = useUpdateTenant(tenantId);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const f of [...TEXT_FIELDS, ...NUM_FIELDS]) {
      const v = (preferences as Record<string, unknown>)[f.key];
      init[f.key] = v != null ? String(v) : "";
    }
    return init;
  });

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    const prefs: Record<string, string | number> = {};
    for (const f of TEXT_FIELDS) if (form[f.key].trim()) prefs[f.key] = form[f.key].trim();
    for (const f of NUM_FIELDS) if (form[f.key].trim()) prefs[f.key] = Number(form[f.key]);
    update.mutate(
      { preferences: prefs },
      {
        onSuccess: () => {
          toast.success("Search preferences saved");
          setEditing(false);
        },
        onError: () => toast.error("Could not save preferences"),
      },
    );
  };

  const text = summary(preferences);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Search preferences</CardTitle>
        {!editing && (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
            <Pencil className="size-4" />
            Edit
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {editing ? (
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {TEXT_FIELDS.map((f) => (
                <div key={f.key} className="space-y-1.5">
                  <Label htmlFor={f.key}>{f.label}</Label>
                  <Input
                    id={f.key}
                    placeholder={f.placeholder}
                    value={form[f.key]}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  />
                </div>
              ))}
              {NUM_FIELDS.map((f) => (
                <div key={f.key} className="space-y-1.5">
                  <Label htmlFor={f.key}>{f.label}</Label>
                  <Input
                    id={f.key}
                    type="number"
                    step="any"
                    value={form[f.key]}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={update.isPending}>
                {update.isPending ? "Saving…" : "Save preferences"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setEditing(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : text ? (
          <p className="text-sm">{text}</p>
        ) : (
          <p className="text-sm text-muted-foreground">
            No search preferences set — add them so this tenant can be matched to new
            listings.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
